import { z } from 'zod'

// ============================================================================
// Metric Item Schema
// ============================================================================

export const MetricItemSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  unit: z.string().optional(),
  format: z.enum(['currency_inr', 'percent', 'number']).optional(),
  trend: z.enum(['up', 'down', 'stable']).optional(),
  change: z.string().optional(),
})

export type MetricItem = z.infer<typeof MetricItemSchema>

// ============================================================================
// Metrics Block Schema
// ============================================================================

export const MetricsBlockSchema = z.object({
  type: z.literal('metrics'),
  title: z.string().optional(),
  data: z.object({
    items: z.array(MetricItemSchema),
  }),
})

export type MetricsBlock = z.infer<typeof MetricsBlockSchema>

// ============================================================================
// Table Column Schema
// ============================================================================

export const TableColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['string', 'number', 'date', 'badge', 'link']).optional(),
  sortable: z.boolean().optional(),
})

export type TableColumn = z.infer<typeof TableColumnSchema>

// ============================================================================
// Table Block Schema
// ============================================================================

export const TableBlockSchema = z.object({
  type: z.literal('table'),
  title: z.string().optional(),
  data: z.object({
    columns: z.array(TableColumnSchema),
    rows: z.array(z.record(z.unknown())),
    pagination: z
      .object({
        page: z.number(),
        pageSize: z.number(),
        total: z.number(),
      })
      .optional(),
  }),
})

export type TableBlock = z.infer<typeof TableBlockSchema>

// ============================================================================
// Note Block Schema
// ============================================================================

export const NoteBlockSchema = z.object({
  type: z.literal('note'),
  variant: z.enum(['info', 'success', 'warning', 'error']).default('info'),
  data: z.object({
    title: z.string().optional(),
    text: z.string(),
    link: z
      .object({
        text: z.string(),
        url: z.string(),
      })
      .optional(),
  }),
})

export type NoteBlock = z.infer<typeof NoteBlockSchema>

// ============================================================================
// Action Item Schema
// ============================================================================

export const ActionItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  variant: z.enum(['primary', 'secondary', 'danger']).optional(),
  action: z.object({
    type: z.enum(['navigate', 'api_call', 'copy', 'download']),
    url: z.string().optional(),
    payload: z.record(z.unknown()).optional(),
  }),
})

export type ActionItem = z.infer<typeof ActionItemSchema>

// ============================================================================
// Actions Block Schema
// ============================================================================

export const ActionsBlockSchema = z.object({
  type: z.literal('actions'),
  title: z.string().optional(),
  data: z.object({
    items: z.array(ActionItemSchema),
  }),
})

export type ActionsBlock = z.infer<typeof ActionsBlockSchema>

// ============================================================================
// Markdown Block Schema
// ============================================================================

export const MarkdownBlockSchema = z.object({
  type: z.literal('markdown'),
  title: z.string().optional(),
  data: z.object({
    content: z.string(),
  }),
})

export type MarkdownBlock = z.infer<typeof MarkdownBlockSchema>

// ============================================================================
// Raw JSON Block Schema
// ============================================================================

export const RawJsonBlockSchema = z.object({
  type: z.literal('raw_json'),
  title: z.string().optional(),
  data: z.object({
    json: z.record(z.unknown()),
    collapsed: z.boolean().optional(),
  }),
})

export type RawJsonBlock = z.infer<typeof RawJsonBlockSchema>

// ============================================================================
// Graph Path Schema (for Knowledge Graph visualization)
// ============================================================================

export const GraphPathSchema = z.object({
  subject: z.string(),
  predicate: z.string(),
  object: z.string(),
  properties: z.record(z.unknown()).optional(),
})

export type GraphPath = z.infer<typeof GraphPathSchema>

// ============================================================================
// Graph Block Schema
// ============================================================================

export const GraphBlockSchema = z.object({
  type: z.literal('graph'),
  title: z.string().optional(),
  data: z.object({
    paths: z.array(GraphPathSchema),
    layout: z.enum(['dagre', 'force', 'tree']).optional(),
    height: z.number().optional(),
  }),
})

export type GraphBlock = z.infer<typeof GraphBlockSchema>

// ============================================================================
// HITL: Approval Block Schema
// ============================================================================

export const ApprovalBlockSchema = z.object({
  type: z.literal('approval'),
  data: z.object({
    request_id: z.string(),
    action: z.string(),
    approver_role: z.string().optional(),
    message: z.string(),
  }),
})

export type ApprovalBlock = z.infer<typeof ApprovalBlockSchema>

// ============================================================================
// HITL: Input Request Block Schema
// ============================================================================

export const InputRequestBlockSchema = z.object({
  type: z.literal('input_request'),
  data: z.object({
    request_id: z.string(),
    prompt: z.string(),
    input_type: z.enum(['text', 'choice', 'confirmation']),
    options: z.array(z.string()).optional(),
    default: z.string().optional(),
  }),
})

export type InputRequestBlock = z.infer<typeof InputRequestBlockSchema>

// ============================================================================
// Union of All Block Types
// ============================================================================

export const UIBlockSchema = z.discriminatedUnion('type', [
  MetricsBlockSchema,
  TableBlockSchema,
  NoteBlockSchema,
  ActionsBlockSchema,
  MarkdownBlockSchema,
  RawJsonBlockSchema,
  GraphBlockSchema,
  ApprovalBlockSchema,
  InputRequestBlockSchema,
])

export type UIBlock = z.infer<typeof UIBlockSchema>

// ============================================================================
// Block Type Literals
// ============================================================================

export type BlockType = UIBlock['type']

export const BLOCK_TYPES: readonly BlockType[] = [
  'metrics',
  'table',
  'note',
  'actions',
  'markdown',
  'raw_json',
  'graph',
  'approval',
  'input_request',
] as const

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Safely parse a block from unknown data
 * Returns the parsed block or null if parsing fails
 */
export function parseBlock(data: unknown): UIBlock | null {
  const result = UIBlockSchema.safeParse(data)
  if (result.success) {
    return result.data
  }
  console.warn('Failed to parse block:', result.error.errors)
  return null
}

/**
 * Type guard for checking block type
 */
export function isBlockType<T extends BlockType>(
  block: UIBlock,
  type: T
): block is Extract<UIBlock, { type: T }> {
  return block.type === type
}
